import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { addYears, format, startOfMonth } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import BackButton from '@sophocles/components/BackButton';
import { AddressDisplayCard, ContactForm } from '@sophocles/components/contacts';
import CustomerTypeSelect from '@sophocles/components/forms/CustomerTypeSelect';
import UnifiedPricingManager from '@sophocles/components/UnifiedPricingManager';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import { fetchAllUserSellers } from '@sophocles/entities/User';
import { selectAllCustomerTypes } from '@sophocles/features/customers/slice';
import { useCreateCustomerMutation } from '@sophocles/features/entities/customers';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import { parseApiError } from '@sophocles/utils/apiErrors';
import BillingFrequencySelect from '@sophocles/views/billing/components/BillingFrequencySelect';
import InventoryPolicySelect from '@sophocles/views/billing/components/InventoryPolicySelect';

const ReviewTable = ({ data }) => (
  <TableContainer component={Paper} sx={{ mb: 3 }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <strong>Field</strong>
          </TableCell>
          <TableCell>
            <strong>Value</strong>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map(({ field, value }) => (
          <TableRow key={field}>
            <TableCell>{field}</TableCell>
            <TableCell>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

function CreateCustomerView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [users, setUsers] = useState([]);
  const [submissionError, setSubmissionError] = useState(null);
  const [editingShipping, setEditingShipping] = useState(false);
  const [editingBilling, setEditingBilling] = useState(false);

  const [createCustomer] = useCreateCustomerMutation();

  const customerTypes = useSelector(selectAllCustomerTypes);
  const separateTenant = useSelector(selectSeparateTenant);

  useEffect(() => {
    dispatch(fetchAllUserSellers()).then(res => setUsers(res));
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    const valuesToSubmit = {
      is_prospect: false,
      ...values.details,
      ...(!__EXCLUDE_BILLING__ ? values.billing : {}),
    };
    if (valuesToSubmit.is_billboard == null) {
      valuesToSubmit.is_billboard = false;
    }
    if (valuesToSubmit.inventory_policy != 'percentage') {
      valuesToSubmit.inventory_amount = 0;
    }
    if (valuesToSubmit.annual_start_date != null) {
      valuesToSubmit.annual_start_date = format(valuesToSubmit.annual_start_date, 'yyyy-MM-01');
    }

    // Add contact data to payload - backend handles creation
    valuesToSubmit.shipping_contact = values.shippingContact;
    valuesToSubmit.billing_contact = values.sameAsShipping ? null : values.billingContact;
    valuesToSubmit.same_billing_as_shipping = values.sameAsShipping;

    if (!__EXCLUDE_BILLING__) {
      // Add pricing configuration - backend expects top-level fields
      // Only include configs that have a price set (pricing_unit defaults to PER_DEVICE for devices)
      valuesToSubmit.device_pricing_configs = (values.pricingConfig?.deviceConfigs || [])
        .filter(c => c.default_price && c.default_price !== '0' && c.default_price !== '')
        .map(c => ({
          category_id: c.category?.id,
          pricing_unit: c.pricing_unit || 'PER_DEVICE', // Default to PER_DEVICE if not explicitly set
          default_price: c.default_price,
          overrides: (c.overrides || []).map(o => ({
            site_group_id: o.site_group?.id || null,
            site_id: o.site?.id || null,
            price: o.price,
          })),
        }));
      valuesToSubmit.feature_pricing_configs = (values.pricingConfig?.featureConfigs || [])
        .filter(c => c.pricing_unit && c.default_price && c.default_price !== '0' && c.default_price !== '')
        .map(c => ({
          feature_key: c.feature?.key,
          pricing_unit: c.pricing_unit,
          device_type: c.device_type,
          default_price: c.default_price,
          overrides: (c.overrides || []).map(o => ({
            site_group_id: o.site_group?.id || null,
            site_id: o.site?.id || null,
            price: o.price,
          })),
        }));
    }

    // setSubmitting stays true on the success path: the view navigates away, so
    // releasing it would re-enable Create for the frames before the route swaps.
    try {
      const result = await createCustomer({ body: valuesToSubmit });
      if (result.error) {
        const { fieldErrors, nonFieldMessages } = parseApiError(result.error, {
          genericMessage: t('customers.createError', 'An error occurred while creating the customer'),
        });
        const fieldMessages = Object.entries(fieldErrors).map(
          ([field, messages]) => `${field} - ${messages.join(', ')}`,
        );
        setSubmissionError([...fieldMessages, ...nonFieldMessages].join('\n'));
        setSubmitting(false);
        return;
      }
      // Navigate directly - backend handles contact/address creation
      navigate(`/c/${result.data.id}/settings?tab=5`, { replace: true });
    } catch (error) {
      // A rejection here would otherwise leave isSubmitting true and Create
      // disabled for good, stranding the operator on a filled-in form.
      const { nonFieldMessages } = parseApiError(error, {
        genericMessage: t('customers.createError', 'An error occurred while creating the customer'),
      });
      setSubmissionError(nonFieldMessages.join('\n'));
      setSubmitting(false);
    }
  };

  return (
    <AppNav loading={false} notFullHeight page="customers" title={t('customers.createOrganization')}>
      <TitleBar
        title={
          <div>
            <BackButton /> {t('customers.createOrganization')}
          </div>
        }
      />
      <Formik
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          details: Yup.object().shape({
            name: Yup.string().required('Name is required'),
            description: Yup.string(),
            customer_type: separateTenant
              ? Yup.number().nullable()
              : Yup.number().required('Customer Type is required').typeError('Must be a number'),
            is_billboard: Yup.boolean(),
          }),

          ...(!__EXCLUDE_BILLING__
            ? {
                billing: Yup.object().shape({
                  primary_sales_person: Yup.number().required('Salesperson is required').typeError('Must be a number'),
                  start_date: Yup.string().required('Start Date is required'),
                  end_date: Yup.string().required('End Date is required'),
                  billing_frequency: Yup.string().required('Billing Frequency is required'),

                  annual_start_date: Yup.string()
                    .nullable()
                    .when('billing_frequency', (billing_frequency, schema) =>
                      billing_frequency === 'annual'
                        ? schema.required('Annual Billing Month is required')
                        : schema.nullable(),
                    ),

                  pricing_guarantee_start_date: Yup.string().required('Pricing Guarantee Start Date is required'),
                  pricing_guarantee_end_date: Yup.string().required('Pricing Guarantee End Date is required'),
                  inventory_policy: Yup.string().required('Inventory Policy is required'),
                  inventory_amount: Yup.string().required('Inventory Amount is required'),
                }),
              }
            : {}),
        })}
        initialValues={{
          details: {
            name: '',
            description: '',
            // Hidden on separate-tenant deploys; null lets the backend apply
            // the tenant-configured default customer type ('' fails Yup's
            // number cast and blocks the submit).
            customer_type: separateTenant ? null : '',
            bill_plants_separately: false,
          },
          // Shipping contact - populated via ContactForm dialog
          shippingContact: null,
          // Billing contact - populated via ContactForm dialog
          billingContact: null,
          // Whether billing contact is same as shipping
          sameAsShipping: false,
          ...(!__EXCLUDE_BILLING__
            ? {
                billing: {
                  agreement_signed: false,
                  primary_sales_person: null,
                  start_date: format(new Date(), 'yyyy-MM-dd'),
                  end_date: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
                  billing_frequency: 'month',
                  annual_start_date: null,
                  pricing_guarantee_start_date: format(new Date(), 'yyyy-MM-dd'),
                  pricing_guarantee_end_date: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
                  inventory_policy: 'exact',
                  inventory_amount: 0,
                  is_billed_virtual: false,
                },
                // Pricing configuration (populated via UnifiedPricingManager)
                pricingConfig: {
                  deviceConfigs: [],
                  featureConfigs: [],
                },
              }
            : {}),
        }}
      >
        {({
          values,
          setFieldValue,
          submitForm,
          resetForm,
          isSubmitting,
          touched,
          errors,
          dirty,
          isValid,
          handleChange,
          handleBlur,
        }) => {
          const customerType = customerTypes.find(type => type.id === values.details.customer_type);
          const salesPerson = !__EXCLUDE_BILLING__
            ? users.find(user => user.id === values.billing.primary_sales_person)
            : null;

          // Preparing data for each section to display in the table format
          const detailData = [
            { field: 'Name', value: values.details.name },
            { field: 'Description', value: values.details.description },
            // Customer Type / Billboard / per-site-group billing don't apply to
            // separate tenants (the inputs are hidden), so keep them out of the
            // review too.
            ...(separateTenant
              ? []
              : [
                  { field: 'Customer Type', value: customerType?.name ?? 'Unknown' },
                  { field: 'Is Billboard', value: values.details.is_billboard ? 'Yes' : 'No' },
                  {
                    field: 'Bill Site Groups Separately',
                    value: values.details.bill_plants_separately ? 'Yes' : 'No',
                  },
                ]),
          ];

          const shippingContactData = values.shippingContact
            ? [
                { field: 'Name', value: values.shippingContact.name || '-' },
                { field: 'Business Name', value: values.shippingContact.business_name || '-' },
                { field: 'Email', value: values.shippingContact.email || '-' },
                { field: 'Phone', value: values.shippingContact.phone1 || '-' },
                { field: 'Address', value: values.shippingContact.address || '-' },
                { field: 'City', value: values.shippingContact.city || '-' },
                { field: 'State', value: values.shippingContact.state || '-' },
                { field: 'ZIP', value: values.shippingContact.zipcode || '-' },
                { field: 'Country', value: values.shippingContact.country || '-' },
              ]
            : [{ field: 'Info', value: 'No shipping contact provided' }];

          const billingContactData = values.sameAsShipping
            ? [{ field: 'Info', value: 'Same as Shipping' }]
            : values.billingContact
              ? [
                  { field: 'Name', value: values.billingContact.name || '-' },
                  { field: 'Business Name', value: values.billingContact.business_name || '-' },
                  { field: 'Email', value: values.billingContact.email || '-' },
                  { field: 'Phone', value: values.billingContact.phone1 || '-' },
                  { field: 'Address', value: values.billingContact.address || '-' },
                  { field: 'City', value: values.billingContact.city || '-' },
                  { field: 'State', value: values.billingContact.state || '-' },
                  { field: 'ZIP', value: values.billingContact.zipcode || '-' },
                  { field: 'Country', value: values.billingContact.country || '-' },
                ]
              : [{ field: 'Info', value: 'No billing contact provided' }];

          // Build pricing config review data - show each category with its price
          const PRICING_UNIT_LABELS = !__EXCLUDE_BILLING__
            ? {
                FLAT: 'Flat Fee',
                PER_SITE_GROUP: 'Per Site Group',
                PER_SITE: 'Per Site',
                PER_DEVICE: 'Per Device',
                PER_USER: 'Per User',
              }
            : {};

          const billingData = !__EXCLUDE_BILLING__
            ? [
                { field: 'Is Agreement Signed', value: values.billing.agreement_signed ? 'Yes' : 'No' },
                {
                  field: 'Salesperson',
                  value: `${salesPerson?.first_name ?? ''} ${salesPerson?.last_name ?? ''} (${salesPerson?.id ?? ''})`,
                },
                { field: 'Start Date', value: values.billing.start_date },
                { field: 'End Date', value: values.billing.end_date },
                { field: 'Billing Frequency', value: values.billing.billing_frequency },
                {
                  field: 'Annual Billing Month',
                  value: values.billing.annual_start_date ? format(values.billing.annual_start_date, 'MMMM') : 'N/A',
                },
                {
                  field: 'Pricing Guarantee Start Date',
                  value: values.billing.pricing_guarantee_start_date,
                },
                {
                  field: 'Pricing Guarantee End Date',
                  value: values.billing.pricing_guarantee_end_date,
                },
                { field: 'Virtual Billing', value: values.billing.is_billed_virtual ? 'Enabled' : 'Disabled' },
              ]
            : [];

          const inventoryData = !__EXCLUDE_BILLING__
            ? [
                { field: 'Inventory Policy', value: values.billing.inventory_policy },
                { field: 'Inventory Amount', value: values.billing.inventory_amount },
              ]
            : [];

          const devicePricingData = !__EXCLUDE_BILLING__
            ? (values.pricingConfig?.deviceConfigs || []).map(c => ({
                field: c.category?.name || 'Unknown Category',
                value:
                  c.default_price && c.default_price !== '0'
                    ? `$${parseFloat(c.default_price).toFixed(2)} (${PRICING_UNIT_LABELS[c.pricing_unit] || PRICING_UNIT_LABELS.PER_DEVICE})`
                    : 'Not configured',
              }))
            : [];

          const featurePricingData = !__EXCLUDE_BILLING__
            ? (values.pricingConfig?.featureConfigs || []).map(c => ({
                field: c.feature?.name || 'Unknown Feature',
                value:
                  c.pricing_unit && c.default_price && c.default_price !== '0'
                    ? `$${parseFloat(c.default_price).toFixed(2)} (${PRICING_UNIT_LABELS[c.pricing_unit] || 'Unknown'})`
                    : 'Not configured',
              }))
            : [];

          return (
            <Form>
              <Container maxWidth="md" sx={{ py: 3 }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
                  <Stepper activeStep={stepIndex}>
                    <Step>
                      <StepLabel>{t('general.details')}</StepLabel>
                    </Step>
                    {!__EXCLUDE_BILLING__ && (
                      <Step>
                        <StepLabel>{t('customers.billing')}</StepLabel>
                      </Step>
                    )}
                    <Step>
                      <StepLabel>{t('customers.review')}</StepLabel>
                    </Step>
                  </Stepper>
                  <Box sx={{ py: 3 }}>
                    {submissionError && (
                      <Alert severity="error" onClose={() => setSubmissionError(null)}>
                        {submissionError}
                      </Alert>
                    )}
                    {/* Show all Formik errors on the review screen */}
                    {stepIndex === (!__EXCLUDE_BILLING__ ? 2 : 1) && Object.keys(errors).length > 0 && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>{t('customers.formErrors', 'Form Errors:')}</strong>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {Object.entries(errors).flatMap(([section, sectionErrors]) => {
                            if (sectionErrors && typeof sectionErrors === 'object') {
                              return Object.entries(sectionErrors)
                                .filter(([_, error]) => !!error)
                                .map(([field, error]) => (
                                  <li key={section + '.' + field}>
                                    {section.charAt(0).toUpperCase() + section.slice(1)} - {field}: {error}
                                  </li>
                                ));
                            } else if (sectionErrors) {
                              return (
                                <li key={section}>
                                  {section.charAt(0).toUpperCase() + section.slice(1)}: {sectionErrors}
                                </li>
                              );
                            } else {
                              return null;
                            }
                          })}
                        </ul>
                      </Alert>
                    )}
                    {stepIndex === 0 && (
                      <>
                        <Typography variant="h5">{t('general.details')}</Typography>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                          {t('customers.fillOutGeneralInfo')}
                        </Typography>

                        <Stack spacing={2}>
                          <Field name="details.name">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                size="small"
                                label="Name"
                                required
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error ? meta.error : ''}
                              />
                            )}
                          </Field>

                          <Field name="details.description">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                size="small"
                                label="Description"
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error ? meta.error : ''}
                              />
                            )}
                          </Field>

                          {!separateTenant && (
                            <CustomerTypeSelect
                              required
                              size="small"
                              name="details.customer_type"
                              value={values.details?.customer_type}
                              error={touched.details?.customer_type && Boolean(errors.details?.customer_type)}
                              helperText={touched.details?.customer_type && errors.details?.customer_type}
                              onBlur={e => {
                                handleBlur(e);
                              }}
                              onChange={e => {
                                setFieldValue('details.customer_type', e.target.value);
                              }}
                            />
                          )}

                          {!separateTenant && (
                            <Field name="details.is_billboard">
                              {({ field, form }) => (
                                <FormControlLabel
                                  sx={{ margin: 0, alignItems: 'center' }}
                                  control={
                                    <Checkbox
                                      {...field}
                                      checked={Boolean(field.value)}
                                      onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                    />
                                  }
                                  label={t('customers.isBillboard', 'Is Billboard?')}
                                />
                              )}
                            </Field>
                          )}

                          {!separateTenant && (
                            <Field name="details.bill_plants_separately">
                              {({ field }) => (
                                <FormControlLabel
                                  sx={{ margin: 0, alignItems: 'center' }}
                                  control={<Checkbox {...field} checked={field.value} />}
                                  label={t('customers.billSeparately', 'Bill Site Groups Separately')}
                                />
                              )}
                            </Field>
                          )}

                          {!separateTenant && (
                            <>
                              {/* Contact Information Section */}
                              <Typography variant="h5">{t('customers.contactInformation')}</Typography>

                              {/* Shipping Contact */}
                              <AddressDisplayCard
                                contact={values.shippingContact}
                                label={t('customers.shippingContact', 'Shipping Contact')}
                                required={false}
                                showAddress={true}
                                emptyText="No contact entered"
                                emptyClickText="Click to enter details"
                                hideChangeLink={true}
                                onChange={() => setEditingShipping(true)}
                                onEdit={() => setEditingShipping(true)}
                              />
                              <ContactForm
                                open={editingShipping}
                                onClose={() => setEditingShipping(false)}
                                contact={values.shippingContact}
                                standaloneMode={true}
                                primaryContext="none"
                                requiredFields={[]}
                                useOnceOnly={true}
                                validateIfAnyFilled={true}
                                onSuccess={contact => {
                                  setFieldValue('shippingContact', contact);
                                  // If "same as shipping" is checked, also update billing
                                  if (values.sameAsShipping) {
                                    setFieldValue('billingContact', contact);
                                  }
                                  setEditingShipping(false);
                                }}
                              />

                              {/* Same as Shipping Checkbox */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.sameAsShipping}
                                    onChange={e => {
                                      setFieldValue('sameAsShipping', e.target.checked);
                                      if (e.target.checked && values.shippingContact) {
                                        // Copy shipping contact to billing contact
                                        setFieldValue('billingContact', { ...values.shippingContact });
                                      }
                                    }}
                                  />
                                }
                                label={t('customers.billingSameAsShipping', 'Billing contact same as shipping')}
                                sx={{ mb: 1 }}
                              />

                              {/* Billing Contact (shown only when not same as shipping) */}
                              {!values.sameAsShipping && (
                                <>
                                  <AddressDisplayCard
                                    contact={values.billingContact}
                                    label={t('customers.billingContact', 'Billing Contact')}
                                    required={false}
                                    showAddress={true}
                                    emptyText="No contact entered"
                                    emptyClickText="Click to enter details"
                                    hideChangeLink={true}
                                    onChange={() => setEditingBilling(true)}
                                    onEdit={() => setEditingBilling(true)}
                                  />
                                  <ContactForm
                                    open={editingBilling}
                                    onClose={() => setEditingBilling(false)}
                                    contact={values.billingContact}
                                    standaloneMode={true}
                                    primaryContext="none"
                                    requiredFields={[]}
                                    useOnceOnly={true}
                                    validateIfAnyFilled={true}
                                    onSuccess={contact => {
                                      setFieldValue('billingContact', contact);
                                      setEditingBilling(false);
                                    }}
                                  />
                                </>
                              )}
                            </>
                          )}
                        </Stack>

                        <Stack direction="row" spacing={2} justifyContent="end" sx={{ mt: 3 }}>
                          <Button
                            disabled={values.details.name === '' || (!separateTenant && !values.details.customer_type)}
                            onClick={() => {
                              setStepIndex(1);
                            }}
                          >
                            {t('actions.next')}
                          </Button>
                        </Stack>
                      </>
                    )}
                    {!__EXCLUDE_BILLING__ && stepIndex === 1 && (
                      <Stack spacing={2}>
                        <Field name="billing.agreement_signed">
                          {({ field }) => (
                            <FormControlLabel
                              sx={{ margin: 0, alignItems: 'center' }}
                              control={<Checkbox {...field} checked={field.value} />}
                              label={t('customers.isAgreementSigned', 'Is Agreement Signed?')}
                            />
                          )}
                        </Field>
                        <UserAutoComplete
                          fullWidth
                          required
                          name="billing.primary_sales_person"
                          label="Salesperson"
                          value={values.billing.primary_sales_person}
                          users={users}
                          sx={{ flex: 1 }}
                          onChange={e => {
                            if (e.target.value === '') {
                              e.target.value = null;
                            }
                            handleChange(e);
                          }}
                        />
                        <Field name="billing.start_date">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              required
                              type="date"
                              label={t('customers.startDate', 'Start Date')}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>

                        <Field name="billing.end_date">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              required
                              type="date"
                              label={t('customers.endDate', 'End Date')}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>
                        <Divider />
                        <Typography variant="h6">{t('customers.billingSchedule', 'Billing Schedule')}</Typography>
                        <BillingFrequencySelect
                          hideAll
                          size="small"
                          value={values.billing.billing_frequency}
                          onChange={newFrequency => setFieldValue('billing.billing_frequency', newFrequency)}
                        />
                        {values.billing.billing_frequency === 'annual' && (
                          <Field name="billing.annual_start_date">
                            {({ field, form }) => (
                              <DatePicker
                                views={['month']} // Restricts selection to year & month
                                label={t('customers.annualStartMonth', 'Annual Start Month')}
                                value={field.value}
                                onChange={date => {
                                  if (date) {
                                    form.setFieldValue(field.name, startOfMonth(date)); // Store as yyyy-MM-01
                                  } else {
                                    form.setFieldValue(field.name, ''); // Handle clearing value
                                  }
                                }}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    error:
                                      form.touched.billing.annual_start_date &&
                                      Boolean(form.errors.billing.annual_start_date),
                                    helperText:
                                      form.touched.billing.annual_start_date && form.errors.billing.annual_start_date,
                                  },
                                }}
                              />
                            )}
                          </Field>
                        )}
                        <Field name="billing.pricing_guarantee_start_date">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              type="date"
                              label={t('customers.pricingGuaranteeStart', 'Pricing Guarantee Start Date')}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>

                        <Field name="billing.pricing_guarantee_end_date">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              type="date"
                              label={t('customers.pricingGuaranteeEnd', 'Pricing Guarantee End Date')}
                              size="small"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error ? meta.error : ''}
                            />
                          )}
                        </Field>
                        <Divider />
                        <Typography variant="h6">Inventory</Typography>
                        <InventoryPolicySelect
                          required
                          value={values.billing.inventory_policy}
                          error={touched.billing?.inventory_policy && Boolean(errors.billing?.inventory_policy)}
                          helperText={touched.billing?.inventory_policy && errors.billing?.inventory_policy}
                          onChange={v => {
                            setFieldValue('billing.inventory_policy', v);
                          }}
                        />
                        {values.billing.inventory_policy != 'none' && values.billing.inventory_policy != 'exact' && (
                          <Field name="billing.inventory_amount">
                            {({ field, form, meta }) => (
                              <TextField
                                {...field}
                                required
                                size="small"
                                label={t('customers.inventoryAmount', 'Inventory Amount')}
                                fullWidth
                                onChange={event => form.setFieldValue(field.name, event.target.value)}
                                InputProps={
                                  form.values.billing.inventory_policy === 'percentage'
                                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                    : null
                                }
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error ? meta.error : ''}
                              />
                            )}
                          </Field>
                        )}

                        <Divider />
                        <Typography variant="h6">{t('customers.virtualBilling', 'Virtual Billing')}</Typography>
                        <Field name="billing.is_billed_virtual">
                          {({ field, form }) => (
                            <FormControlLabel
                              sx={{ margin: 0, alignItems: 'center' }}
                              control={
                                <Checkbox
                                  {...field}
                                  checked={Boolean(field.value)}
                                  onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                />
                              }
                              label={t('customers.enableVirtualBilling', 'Enable Virtual Device Billing')}
                            />
                          )}
                        </Field>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                          When enabled, virtual devices will be billed in addition to physical devices.
                        </Typography>

                        <Divider />
                        <Typography variant="h6">
                          {t('customers.pricingConfiguration', 'Pricing Configuration')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Configure default pricing for device categories and features. You can set prices now or adjust
                          them later.
                        </Typography>
                        <UnifiedPricingManager
                          value={values.pricingConfig}
                          onChange={newConfig => setFieldValue('pricingConfig', newConfig)}
                          isBilledVirtual={values.billing.is_billed_virtual}
                          inventoryPolicy={values.billing.inventory_policy}
                          alwaysEditMode={true}
                        />

                        <Stack direction="row" spacing={2} justifyContent="end" sx={{ mt: 3 }}>
                          <Button
                            disabled={
                              values.billing.primary_sales_person == null ||
                              values.billing.start_date === '' ||
                              values.billing.end_date === ''
                            }
                            onClick={() => {
                              setStepIndex(2);
                            }}
                          >
                            {t('actions.next')}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                    {stepIndex === (!__EXCLUDE_BILLING__ ? 2 : 1) && (
                      <Stack spacing={2}>
                        <Typography variant="h5">{t('customers.review')}</Typography>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                          {t('customers.reviewAndCreate')}
                        </Typography>

                        <Typography variant="h6">{t('customers.details', 'Details')}</Typography>
                        <ReviewTable data={detailData} />

                        {!separateTenant && (
                          <>
                            <Typography variant="h6">{t('customers.shippingContact', 'Shipping Contact')}</Typography>
                            <ReviewTable data={shippingContactData} />

                            <Typography variant="h6">{t('customers.billingContact', 'Billing Contact')}</Typography>
                            <ReviewTable data={billingContactData} />
                          </>
                        )}

                        {!__EXCLUDE_BILLING__ && (
                          <>
                            <Typography variant="h6">{t('customers.billing', 'Billing')}</Typography>
                            <ReviewTable data={billingData} />

                            <Typography variant="h6">Inventory</Typography>
                            <ReviewTable data={inventoryData} />

                            <Typography variant="h6">{t('customers.devicePricing', 'Device Pricing')}</Typography>
                            <ReviewTable data={devicePricingData} />

                            {featurePricingData.length > 0 && (
                              <>
                                <Typography variant="h6">{t('customers.featurePricing', 'Feature Pricing')}</Typography>
                                <ReviewTable data={featurePricingData} />
                              </>
                            )}
                          </>
                        )}

                        <Stack direction="row" spacing={2} justifyContent="end" sx={{ mt: 3 }}>
                          <Button onClick={submitForm} disabled={isSubmitting}>
                            {t('actions.create')}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Container>
            </Form>
          );
        }}
      </Formik>
    </AppNav>
  );
}

export default CreateCustomerView;
